import { useLiveQuery } from 'dexie-react-hooks'
import React, { useEffect, useState } from 'react'

import { Button, GridRow } from '@mytours/components'
import classNames from 'classnames'

import ConfirmationModal from '../../common/components/confirmation-modal'
import db from '../../db'
import { DexieDownload } from '../../db/types'
import { getDirMode } from '../../platform'
import strings from '../../../strings'
import { formatBytes } from '../functions'
import { PROJECT_KEY, remove } from '../index'
import { offlineSupported } from '../../../sw/register'

import './offlineStorage.less'

const NO_ROWS: DexieDownload[] = []

/**
 * What a visitor's downloads are costing them, and the way to get it back.
 *
 * The total comes from the browser rather than from the rows: the rows count what this app stored, and the
 * browser counts everything the origin holds — the shell, the images browsed into the cache on the way
 * past. The rows are what removal works from, since only they know which URL belongs to which tour.
 */
const OfflineStorage: React.FC = () => {
	const [used, setUsed] = useState<number>()
	const [confirmingRemoveAll, setConfirmingRemoveAll] = useState(false)
	const [removing, setRemoving] = useState(false)
	// `useLiveQuery` resolves asynchronously, so it answers `undefined` on the first render whatever
	// initial value it is given. One shared empty array rather than a fresh literal: the effect below
	// depends on this, and a new array per render would re-measure storage on every one of them.
	const rows = useLiveQuery(() => db.downloads.toArray(), [], []) ?? NO_ROWS

	useEffect(() => {
		let current = true

		void navigator.storage
			?.estimate?.()
			.then(estimate => {
				if (current) setUsed(estimate.usage)
			})
			.catch(() => undefined)

		return () => {
			current = false
		}
		// Re-measured whenever a download lands or is removed, which is when the figure has moved.
	}, [rows])

	// Nothing has been downloaded and nothing can be, so there is nothing to manage.
	if (!offlineSupported() || rows.length === 0) return null

	const removeAll = async () => {
		setRemoving(true)
		setConfirmingRemoveAll(false)

		for (const row of rows) await remove(row.key)

		setRemoving(false)
	}

	return (
		<div className="block settings-block-list">
			<div className="block-content offline-storage">
				<GridRow className="offline-storage-total" dir={getDirMode()}>
					<span>{strings.offline_storage}</span>
					<span className="offline-storage-used">
						{strings.formatString(strings.offline_storage_used, { size: formatBytes(used) }) as string}
					</span>
				</GridRow>

				<ul className="offline-storage-rows">
					{rows.map(row => (
						<li key={row.key}>
							<span className="offline-storage-name">{rowName(row)}</span>
							<span className="offline-storage-size">{formatBytes(row.bytes)}</span>
						</li>
					))}
				</ul>

				<Button
					className={classNames('update-metadata-btn', { loading: removing })}
					onClick={() => setConfirmingRemoveAll(true)}
					variant="clear"
					aria-label={strings.remove_all_downloads}
				>
					<GridRow className="update-button-inner" dir={getDirMode()}>
						<i className="load-icon fal fa-circle-trash" />
						<span>{strings.remove_all_downloads}</span>
					</GridRow>
				</Button>
			</div>

			<ConfirmationModal
				isOpen={confirmingRemoveAll}
				question={strings.remove_all_downloads}
				onDidDismiss={() => setConfirmingRemoveAll(false)}
				onAccept={() => void removeAll()}
			/>
		</div>
	)
}

/** The tour's own title where the download recorded one, and the project set named for what it is. */
const rowName = ({ key, title }: DexieDownload): string =>
	title ?? (key === PROJECT_KEY ? strings.offline_storage : strings.downloaded_tour)

export default OfflineStorage
